import { CronExpressionParser } from 'cron-parser';

export type FrequencyType = 'Hourly' | 'Daily' | 'Weekdays' | 'Weekends' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly' | 'Custom';

export const FREQ_TYPES: FrequencyType[] = ['Daily', 'Weekdays', 'Weekends', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'];

// Frequency pills offered in the agent schedule modals (CreateScheduleModal / EditScheduleModal).
// Adds Hourly and Custom (raw cron) on top of FREQ_TYPES without affecting the processed-model ScheduleEditor.
export const AGENT_FREQ_TYPES: FrequencyType[] = ['Hourly', 'Daily', 'Weekdays', 'Weekends', 'Weekly', 'Monthly', 'Quarterly', 'Yearly', 'Custom'];
export const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
export const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

export function parseRuntime(runtime: string): {
  type: FrequencyType;
  hour: string;
  minute: string;
  ampm: 'AM' | 'PM';
  weekDay: string;
  monthDay: number;
  cronExpression: string;
} {
  const defaults = { type: 'Daily' as FrequencyType, hour: '9', minute: '00', ampm: 'AM' as const, weekDay: 'Mon', monthDay: 1, cronExpression: '' };
  const lower = runtime.toLowerCase();

  let type: FrequencyType = 'Daily';
  if (lower.startsWith('hourly')) type = 'Hourly';
  else if (lower.startsWith('custom')) type = 'Custom';
  else if (lower.startsWith('weekdays')) type = 'Weekdays';
  else if (lower.startsWith('weekends')) type = 'Weekends';
  else if (lower.startsWith('weekly')) type = 'Weekly';
  else if (lower.startsWith('monthly')) type = 'Monthly';
  else if (lower.startsWith('quarterly')) type = 'Quarterly';
  else if (lower.startsWith('yearly')) type = 'Yearly';

  // Custom runtimes are stored as "Custom (<cron>)" — recover the raw cron for re-hydration.
  const cronMatch = type === 'Custom' ? runtime.match(/\(([^)]*)\)/) : null;
  const cronExpression = cronMatch ? cronMatch[1].trim() : defaults.cronExpression;

  const timeMatch = runtime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  const hour = timeMatch ? timeMatch[1] : defaults.hour;
  // Hourly runtimes are stored as "Hourly :MM" — recover the minute-past-the-hour.
  const hourlyMinuteMatch = type === 'Hourly' ? runtime.match(/:(\d{2})/) : null;
  const minute = timeMatch ? timeMatch[2] : (hourlyMinuteMatch ? hourlyMinuteMatch[1] : defaults.minute);
  const ampm = timeMatch ? (timeMatch[3].toUpperCase() as 'AM' | 'PM') : defaults.ampm;

  const dayMatch = runtime.match(/\b(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\b/i);
  const weekDay = dayMatch ? dayMatch[1].charAt(0).toUpperCase() + dayMatch[1].slice(1).toLowerCase() : defaults.weekDay;

  const monthDayMatch = runtime.match(/\b(\d+)(st|nd|rd|th)\b/i);
  const monthDay = monthDayMatch ? parseInt(monthDayMatch[1]) : defaults.monthDay;

  return { type, hour, minute, ampm, weekDay, monthDay, cronExpression };
}

export function buildRuntime(
  type: FrequencyType,
  hour: string,
  minute: string,
  ampm: 'AM' | 'PM',
  weekDay: string,
  monthDay: number,
  cronExpression = ''
): string {
  const time = `${hour}:${minute} ${ampm}`;
  const ordinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  if (type === 'Hourly') return `Hourly :${minute}`;
  if (type === 'Custom') return `Custom (${cronExpression})`;
  if (type === 'Daily') return `Daily ${time}`;
  if (type === 'Weekdays') return `Weekdays ${time}`;
  if (type === 'Weekends') return `Weekends ${time}`;
  if (type === 'Weekly') return `Weekly ${splitWeekDays(weekDay).join(', ')} ${time}`;
  if (type === 'Monthly') return `Monthly ${ordinal(monthDay)} ${time}`;
  if (type === 'Quarterly') return `Quarterly ${time}`;
  if (type === 'Yearly') return `Yearly ${time}`;
  return type;
}

const WEEK_DAY_TO_CRON: Record<string, string> = {
  Sun: 'SUN', Mon: 'MON', Tue: 'TUE', Wed: 'WED', Thu: 'THU', Fri: 'FRI', Sat: 'SAT',
};

/** "Wed,Mon" → ["Mon", "Wed"]: known days only, de-duplicated, in WEEK_DAYS order. A Weekly `weekDay` may list several. */
export function splitWeekDays(weekDay: string): string[] {
  const picked = new Set(String(weekDay || '').split(',').map((d) => d.trim()));
  return WEEK_DAYS.filter((day) => picked.has(day));
}

export function buildCronExpression(
  type: FrequencyType,
  hour: string,
  minute: string,
  ampm: 'AM' | 'PM',
  weekDay: string,
  monthDay: number,
  cronExpression = ''
): string {
  // Custom carries the user-entered cron verbatim; Hourly only needs the minute-past-the-hour.
  if (type === 'Custom') return cronExpression.trim();

  const h = ampm === 'PM' && hour !== '12' ? parseInt(hour) + 12 : (ampm === 'AM' && hour === '12' ? 0 : parseInt(hour));
  const m = parseInt(minute);

  // AWS cron: minute hour day-of-month month day-of-week year
  if (type === 'Hourly') return `${m} * * * ? *`;
  if (type === 'Daily') return `${m} ${h} * * ? *`;
  if (type === 'Weekdays') return `${m} ${h} ? * MON-FRI *`;
  if (type === 'Weekends') return `${m} ${h} ? * SAT,SUN *`;
  if (type === 'Weekly') {
    const days = splitWeekDays(weekDay).map((day) => WEEK_DAY_TO_CRON[day]);
    return `${m} ${h} ? * ${days.length > 0 ? days.join(',') : 'MON'} *`;
  }
  if (type === 'Monthly') return `${m} ${h} ${monthDay} * ? *`;
  if (type === 'Quarterly') return `${m} ${h} 1 1/3 ? *`;
  if (type === 'Yearly') return `${m} ${h} 1 1 ? *`;
  return `${m} ${h} * * ? *`;
}

// Validate a cron expression the way computeNextRunFromCron consumes it (AWS 6-field or standard
// 5-field, `?` treated as `*`). Used to gate Custom-cron schedules before save so a bad value can't
// be persisted with an unschedulable nextRunAt.
export function isValidCron(cronExpression: string): boolean {
  const expr = cronExpression?.trim();
  if (!expr) return false;
  try {
    const parts = expr.split(/\s+/);
    if (parts.length !== 5 && parts.length !== 6) return false;
    const standard = (parts.length === 6 ? parts.slice(0, 5) : parts).join(' ').replace(/\?/g, '*');
    CronExpressionParser.parse(standard);
    return true;
  } catch {
    return false;
  }
}

/**
 * The shortest gap in minutes between back-to-back runs of a cron, over its next `samples` runs in its
 * timezone, or null when the cron can't be parsed. `0,5 * * * *` gives 5, although most of its gaps are 55.
 */
export function minCronGapMinutes(cronExpression: string, timezone: string, samples = 50): number | null {
  try {
    const parts = cronExpression.trim().split(/\s+/);
    const standard = (parts.length === 6 ? parts.slice(0, 5) : parts).join(' ').replace(/\?/g, '*');
    const interval = CronExpressionParser.parse(standard, { currentDate: new Date(), tz: timezone });
    let previous = interval.next().toDate().getTime();
    let shortest = Infinity;
    for (let i = 1; i < samples; i++) {
      const next = interval.next().toDate().getTime();
      // A repeated wall-clock time on a DST change can give a zero gap; it is not a real run
      if (next > previous) shortest = Math.min(shortest, (next - previous) / 60000);
      previous = next;
    }
    return Number.isFinite(shortest) ? shortest : null;
  } catch {
    return null;
  }
}

/**
 * The shortest gap in minutes between any two runs of a model's schedules taken together, over the next
 * `windowDays`, or null when there are fewer than two runs in that window. Ten hourly crons at :00, :06, :12 …
 * give 6. Two schedules firing at the same moment give 0 (the model runs twice). A schedule is a cron in its
 * timezone, or a one-off `firstRunTime`.
 */
export function minCombinedRunGapMinutes(
  schedules: Array<{ cronExpression?: string; timezone?: string; firstRunTime?: string }>,
  windowDays = 35,
  maxRunsPerSchedule = 2000
): number | null {
  const now = Date.now();
  const end = now + windowDays * 24 * 60 * 60 * 1000;
  const runs: number[] = [];
  for (const schedule of schedules) {
    if (schedule.cronExpression) {
      try {
        const parts = schedule.cronExpression.trim().split(/\s+/);
        const standard = (parts.length === 6 ? parts.slice(0, 5) : parts).join(' ').replace(/\?/g, '*');
        const interval = CronExpressionParser.parse(standard, { currentDate: new Date(now), tz: schedule.timezone || 'UTC' });
        const own = new Set<number>(); // one cron's repeated wall-clock time on a DST change is one run
        for (let i = 0; i < maxRunsPerSchedule; i++) {
          const at = interval.next().toDate().getTime();
          if (at > end) break;
          own.add(at);
        }
        runs.push(...Array.from(own));
      } catch {
        // an unparseable cron is refused by isValidCron before this runs
      }
    } else if (schedule.firstRunTime) {
      const at = Date.parse(schedule.firstRunTime);
      if (Number.isFinite(at) && at <= end) runs.push(Math.max(at, now));
    }
  }
  if (runs.length < 2) return null;
  runs.sort((a, b) => a - b);
  let shortest = Infinity;
  for (let i = 1; i < runs.length; i++) shortest = Math.min(shortest, (runs[i] - runs[i - 1]) / 60000);
  return shortest;
}

export function computeNextRunFromCron(cronExpression: string, timezone: string): string {
  // AWS cron has 6 fields (minute hour day-of-month month day-of-week year)
  // cron-parser expects standard 5-field cron — strip the year and convert `?` to `*`
  try {
    const parts = cronExpression.trim().split(/\s+/);
    const standard = (parts.length === 6 ? parts.slice(0, 5) : parts)
      .join(' ')
      .replace(/\?/g, '*');

    const interval = CronExpressionParser.parse(standard, {
      currentDate: new Date(),
      tz: timezone,
    });
    return interval.next().toDate().toISOString();
  } catch {
    // Fallback: naive local-time approximation if cron expression is malformed
    const now = new Date();
    const parts = cronExpression.split(' ');
    const m = parseInt(parts[0]);
    const h = parseInt(parts[1]);
    const next = new Date(now);
    next.setHours(h, m, 0, 0);
    if (next <= now) {
      next.setDate(next.getDate() + 1);
    }
    return next.toISOString();
  }
}
